import {
  Box,
  Chip,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import LoaderIcon from 'src/components/LoaderImg/loader.gif';
import emptyImg from 'src/pages/therapist-dashboard/therapist-document/image-removebg-preview.png';
import { physicianConst, physicianTableHeads, PhysicianTableObject } from './table-heads';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import { Stack } from '@mui/system';
import { format } from 'date-fns';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { ClipBoard, DocumentIcons } from 'src/pages/therapist-dashboard/dashboard-icon';
import {
  documentTypesForPhysician,
  physicianStatus,
} from 'src/pages/therapist-dashboard/therapist-document/document-manegment-const';
import {
  getAllPatientByEnterprise,
  getAllPhysicianCompletedDocumentsAction,
  getAllPhysicianDocumentsAction,
  getFacility,
  resetAllPatientReducer,
  resetGetAllFacility,
  resetPhysicianSignCompletedDoc,
  resetPhysicianSignDoc,
  resetUpdateDocumentStatuses,
} from 'src/redux/actions/physicianActions';
import { previewDashboardDoc } from 'src/redux/actions/therapist-dashboard-action';
import { paths } from 'src/paths';
import { PhysicianDTO } from 'src/services/apiServices/physician-service/physician-service';
import { getDisplayString } from 'src/utils/getDaysBtDate';
import { Scrollbar } from '../scrollbar';
import { MultiselectFilter } from './multi-select-filter';
import { MenuDateFilter } from './physician-date-filter';

function getStatus(dueDate: string): string {
  const today: Date = new Date();
  const due: Date = new Date(dueDate);
  const diffTime: number = due.getTime() - today.getTime();
  const diffDays: number = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Convert time difference to days

  if (diffDays >= 31) {
    return 'AWAITING SIGNATURE';
  } else if (diffDays >= 14 && diffDays < 31) {
    return 'UPCOMING';
  } else if (diffDays >= 7 && diffDays < 14) {
    return 'URGENT';
  } else if (diffDays < 7) {
    return 'OVERDUE';
  }

  return 'Unknown status';
}

type SortOptions = {
  key: string;
  direction: 'asc' | 'desc';
};

type PageInfo = {
  pageNo: number;
  pageSize: number;
};

const initialPhysicianDTO: PhysicianDTO = {
  patientIds: [],
  facilityIds: [],
  startDateOfService: null,
  endDateOfService: null,
  startDueDate: null,
  endDueDate: null,
  sortBy: 'dateOfService',
  sortDirection: 'desc',
  page: 0,
  status: null,
  pageSize: 20,
  type: [],
};

import React from 'react';
import { Visibility, VisibilityOutlined } from '@mui/icons-material';
import { setFacilityId } from 'src/hooks/use-facility-id';

type PhysicianDocumentsTableType = {
  source: string;
};

const PhysicianDocumentsTable = (props: PhysicianDocumentsTableType) => {
  const { source } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [physicianDto, setPhysicianDto] = useState<PhysicianDTO>(initialPhysicianDTO);
  const getAllFacilities = useSelector((state: any) => state.getFacilityReducer);
  const completedPhysicianDocument = useSelector(
    (state: any) => state.completedPhysicianDocumentReducer
  );
  const getAllPatient = useSelector((state: any) => state.getAllPatientByEnterpriseReducer);
  const user = useSelector((state: any) => state.getTherapistProfileReducer?.response?.data);
  const allDocumentsOfPhysician = useSelector((state: any) => state.physicianDocumentsReducer);
  const updateDocumentStatuses = useSelector((state: any) => state.updateDocumentStatusesReducer);

  const handleSort = (
    sortoption:
      | 'facilityName'
      | 'patientName'
      | 'documentType'
      | 'dateOfService'
      | 'dueDate'
      | ''
      | 'status',
    direction: SortOptions['direction']
  ) => {
    if (sortoption === '') return;
    setPhysicianDto((prevState: any) => ({
      ...prevState,
      page: 0,
      pageSize: 20,
      sortBy: sortoption,
      sortDirection: direction,
    }));
  };
  const getListOfValues = (items: any) => {
    return items?.map((therapist: any) => therapist?.value);
  };

  function formatString(input: string): string {
    const result = input
      ?.toLowerCase()
      ?.split('_')
      ?.map((word, index) =>
        index === 0 ? word?.charAt(0)?.toUpperCase() + word?.slice(1) : word?.toUpperCase()
      )
      ?.join(' ');

    return result;
  }

  useEffect(() => {
    if (source === physicianConst.COMPLETED) {
      const { patientIds, facilityIds, type, ...resetPhysicianDto } = physicianDto;
      const updatedPhysicianDto = {
        patientIds: getListOfValues(patientIds),
        facilityIds: getListOfValues(facilityIds),
        signatureStatus: physicianConst.COMPLETED,
        type: getListOfValues(type),
        ...resetPhysicianDto,
      };
      dispatch(resetPhysicianSignCompletedDoc());
      dispatch(getAllPhysicianCompletedDocumentsAction(user?.providerId, updatedPhysicianDto));
      return;
    }
    if (source === physicianConst.PENDING) {
      const { patientIds, facilityIds, type, ...resetPhysicianDto } = physicianDto;
      const updatedPhysicianDto = {
        patientIds: getListOfValues(patientIds),
        facilityIds: getListOfValues(facilityIds),
        signatureStatus: physicianConst.PENDING,
        type: getListOfValues(type),
        ...resetPhysicianDto,
      };
      dispatch(resetPhysicianSignDoc());
      dispatch(getAllPhysicianDocumentsAction(user?.providerId, updatedPhysicianDto));
      return;
    }
  }, [physicianDto]);

  // Refresh completed documents when documents are successfully signed
  useEffect(() => {
    if (
      updateDocumentStatuses?.isUpdateSuccess &&
      source === physicianConst.COMPLETED &&
      user?.providerId
    ) {
      const { patientIds, facilityIds, type, ...resetPhysicianDto } = physicianDto;
      const updatedPhysicianDto = {
        patientIds: getListOfValues(patientIds),
        facilityIds: getListOfValues(facilityIds),
        signatureStatus: physicianConst.COMPLETED,
        type: getListOfValues(type),
        ...resetPhysicianDto,
      };
      dispatch(resetPhysicianSignCompletedDoc());
      dispatch(getAllPhysicianCompletedDocumentsAction(user.providerId, updatedPhysicianDto));
      dispatch(resetUpdateDocumentStatuses());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateDocumentStatuses?.isUpdateSuccess, source, user?.providerId, dispatch]);

  const handleSortMultiselect = useCallback((label: any, direction: any) => {
    setPhysicianDto((prevState: any) => ({
      ...prevState,
      page: 0,
      pageSize: 20,
      sortBy:
        label === 'Facility Name'
          ? 'facilityName'
          : label === 'Patient'
          ? 'patientName'
          : label === 'Document Type'
          ? 'documentType'
          : '',
      sortDirection: direction,
    }));
  }, []);

  const handleCallTheList = useCallback((label: any) => {
    if (label === 'Patient') {
      dispatch(resetAllPatientReducer());
      dispatch(
        getAllPatientByEnterprise({
          searchString: '',
          modified: 'asc',
          page: 0,
          pageSize: 20,
        })
      );
      return;
    }
    // getAllPatientByEnterprise
    if (label === 'Facility Name') {
      dispatch(resetGetAllFacility());
      dispatch(
        getFacility({
          page: 0,
          searchString: '',
          pageSize: 20,
        })
      );
    }
  }, []);

  const getAllFacilityList = useCallback(
    (label: any) => {
      if (label === 'Patient') {
        return getAllPatient?.totalPatient?.map((res: any) => {
          return {
            label: `${res?.lastName}, ${res?.firstName}`,
            value: res?.patientId,
          };
        });
      }
      if (label === 'Facility Name') {
        return getAllFacilities?.totalFacility?.map((res: any) => {
          return {
            label: `${res?.name}`,
            value: res?.uuid,
          };
        });
      }
    },
    [getAllFacilities, getAllPatient]
  );

  const handleCallNextApi = (label: any, searchValue: any, isFromSearch: any) => {
    if (label === 'Facility Name') {
      isFromSearch && dispatch(resetGetAllFacility());
      dispatch(
        getFacility({
          page: getAllFacilities?.pageNumber,
          searchString: searchValue,
          pageSize: 20,
        })
      );
    }
    if (label === 'Patient') {
      isFromSearch && dispatch(resetAllPatientReducer());
      dispatch(
        getAllPatientByEnterprise({
          searchString: searchValue,
          modified: 'asc',
          page: getAllPatient?.pageNumber,
          pageSize: 20,
        })
      );
    }
  };

  function calculateDaysRemaining(dos: string): any {
    const dosDate: Date = new Date(dos);
    const currentDate: Date = new Date();
    const endDate: Date = new Date(dosDate);
    endDate.setDate(dosDate.getDate() + 30);

    const timeDiff: number = endDate.getTime() - currentDate.getTime();

    const daysRemaining: number = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    return daysRemaining > 0 ? `${daysRemaining} days` : '-';
  }

  const [selectedDueDate, setSelectedDueDate] = useState<any>({
    startDate: '',
    endDate: '',
  });
  //  THIN-7866: this used to navigate to /physician-dashboard/doc-sign, which rendered the legacy
  //  Jotform iframe with no feature-toggle check at all — the one signing surface the Reform
  //  rollout never gated. It now goes to the same Reform physician screen the v2 table uses.
  //  That screen signs through /provider/signature/bulk-signature/physician/{id}, the only
  //  endpoint that records a PHYSICIAN signature: /sign-document takes an `isSupervisor` boolean
  //  and maps false to SignerRole.THERAPIST, so the therapist Reform screen would sign a
  //  physician's document under the wrong role. Both tables read the same physicianDocumentsReducer
  //  rows, so the clicked row goes over as a one-item selection.
  //
  //  The row travels straight from the click, not through component state. The Reform screen
  //  reads only `location.state.selectedDocuments`, and a setState followed by a navigate in the
  //  same handler would hand it the value from the previous render: nothing on the first click,
  //  the previously clicked document on every click after. The legacy screen never noticed
  //  because it took the document from redux (viewSignatureDashboardDoc) instead — a dispatch
  //  the v2 table does not make and the Reform physician screen does not read.
  const handleSignatureNavigation = (row: any) => {
    setFacilityId(row?.facilityId);
    navigate(paths.physicianDocumentsSign, {
      state: {
        selectedDocuments: [row],
        source: 'PHYSICIAN',
      },
    });
  };

  const getTheDates = (startDate: any, endDate: any) => {
    return {
      startDate,
      endDate,
    };
  };

  const hideFilter: string[] = ['Days Remaining', 'DOS'];

  const previewDocument = (formId: any) => {
    const { documentUrl, ...restDoc } = formId;
    dispatch(previewDashboardDoc({ ...restDoc, pdfUrl: documentUrl }));
    navigate('/physician-dashboard/doc', {
      state: {
        source: 'PHYSICIAN',
      },
    });
  };

  const debounce = <T extends (...args: any[]) => void>(func: T, delay: number): T => {
    let timer: NodeJS.Timeout;
    return ((...args: Parameters<T>) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    }) as T;
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Scrollbar>
        <Stack gap="1rem">
          <TableContainer component={Paper}>
            <Box
              id={
                source === physicianConst.COMPLETED
                  ? physicianConst.COMPLETED
                  : physicianConst.PENDING
              }
              sx={{
                height: 700,
                overflow: 'auto',
                padding: 2,
              }}
            >
              <InfiniteScroll
                dataLength={
                  source === physicianConst.COMPLETED
                    ? completedPhysicianDocument?.totalDocument?.length
                    : allDocumentsOfPhysician?.totalDocument?.length
                }
                // next={() => {
                //   if (source === physicianConst.COMPLETED) {
                //     const { patientIds, page, facilityIds, type, ...resetPhysicianDto } =
                //       physicianDto;
                //     const updatedPhysicianDto = {
                //       patientIds: getListOfValues(patientIds),
                //       facilityIds: getListOfValues(facilityIds),
                //       type: getListOfValues(type),
                //       signatureStatus: physicianConst.COMPLETED,
                //       page: completedPhysicianDocument?.pageNumber,
                //       ...resetPhysicianDto,
                //     };
                //     dispatch(
                //       getAllPhysicianCompletedDocumentsAction(user?.providerId, updatedPhysicianDto)
                //     );
                //     return;
                //   }
                //   if (source === physicianConst.PENDING) {
                //     const { patientIds, page, facilityIds, type, ...resetPhysicianDto } =
                //       physicianDto;
                //     const updatedPhysicianDto = {
                //       patientIds: getListOfValues(patientIds),
                //       facilityIds: getListOfValues(facilityIds),
                //       signatureStatus: physicianConst.PENDING,
                //       type: getListOfValues(type),
                //       page: allDocumentsOfPhysician?.pageNumber,
                //       ...resetPhysicianDto,
                //     };
                //     dispatch(getAllPhysicianDocumentsAction(user?.providerId, updatedPhysicianDto));
                //     return;
                //   }
                // }}
                // style={{
                //   overflow: 'scroll',
                //   height: 700,
                // }}
                // height={800}
                // hasMore={
                //   source === physicianConst.COMPLETED
                //     ? completedPhysicianDocument?.response?.data?.totalElements !==
                //       completedPhysicianDocument?.totalDocument?.length
                //     : allDocumentsOfPhysician?.response?.data?.totalElements !==
                //       allDocumentsOfPhysician?.totalDocument?.length
                // }
                next={function () {
                  throw new Error('Function not implemented.');
                }}
                hasMore={false}
                onScroll={debounce((event) => {
                  const scrollThreshold = 1;

                  if (
                    Math.abs(
                      event.target.scrollHeight - event.target.scrollTop - event.target.clientHeight
                    ) <= scrollThreshold
                  ) {
                    if (
                      source === physicianConst.COMPLETED &&
                      completedPhysicianDocument?.response?.data?.totalElements !==
                        completedPhysicianDocument?.totalDocument?.length
                    ) {
                      // console.log('Loading more completed documents');

                      const { patientIds, page, facilityIds, type, ...resetPhysicianDto } =
                        physicianDto;
                      const updatedPhysicianDto = {
                        patientIds: getListOfValues(patientIds),
                        facilityIds: getListOfValues(facilityIds),
                        type: getListOfValues(type),
                        signatureStatus: physicianConst.COMPLETED,
                        page: completedPhysicianDocument?.pageNumber,
                        ...resetPhysicianDto,
                      };
                      dispatch(
                        getAllPhysicianCompletedDocumentsAction(
                          user?.providerId,
                          updatedPhysicianDto
                        )
                      );
                      return;
                    }

                    // Check if more data should be loaded for pending documents
                    if (
                      source === physicianConst.PENDING &&
                      allDocumentsOfPhysician?.response?.data?.totalElements !==
                        allDocumentsOfPhysician?.totalDocument?.length
                    ) {
                      // console.log('Loading more pending documents');

                      const { patientIds, page, facilityIds, type, ...resetPhysicianDto } =
                        physicianDto;
                      const updatedPhysicianDto = {
                        patientIds: getListOfValues(patientIds),
                        facilityIds: getListOfValues(facilityIds),
                        type: getListOfValues(type),
                        signatureStatus: physicianConst.PENDING,
                        page: allDocumentsOfPhysician?.pageNumber,
                        ...resetPhysicianDto,
                      };
                      dispatch(
                        getAllPhysicianDocumentsAction(user?.providerId, updatedPhysicianDto)
                      );
                      return;
                    }
                  }
                }, 500)} // Adjust the debounce delay (e.g., 500 ms)
                scrollableTarget={
                  source === physicianConst.COMPLETED
                    ? physicianConst.COMPLETED
                    : physicianConst.PENDING
                }
                loader={
                  source === physicianConst.COMPLETED ? (
                    completedPhysicianDocument?.loading ? (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          height: 250,
                          width: '100%',
                        }}
                      >
                        <img
                          src={LoaderIcon}
                          alt="loader.."
                        />
                      </div>
                    ) : !completedPhysicianDocument?.areGetDocuments &&
                      completedPhysicianDocument?.response !== null ? (
                      <Box>
                        <img
                          src={emptyImg}
                          alt="loader"
                          style={{
                            width: 720,
                            height: 300,
                            margin: 'auto',
                            alignItems: 'center',
                            alignContent: 'center',
                            marginTop: 100,
                            display: 'block',
                          }}
                        />

                        <Typography
                          color={'text.secondary'}
                          variant="h5"
                          fontWeight={'200'}
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          Something went wrong
                        </Typography>
                      </Box>
                    ) : null
                  ) : allDocumentsOfPhysician?.loading ? (
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 250,
                        width: '100%',
                      }}
                    >
                      <img
                        src={LoaderIcon}
                        alt="loader.."
                      />
                    </div>
                  ) : !allDocumentsOfPhysician?.areGetDocuments &&
                    allDocumentsOfPhysician?.response !== null ? (
                    <Box>
                      <img
                        src={emptyImg}
                        alt="loader"
                        style={{
                          width: 720,
                          height: 300,
                          margin: 'auto',
                          alignItems: 'center',
                          alignContent: 'center',
                          marginTop: 100,
                          display: 'block',
                        }}
                      />

                      <Typography
                        color={'text.secondary'}
                        variant="h5"
                        fontWeight={'200'}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        Something went wrong
                      </Typography>
                    </Box>
                  ) : null
                }
                endMessage={
                  <Box
                    textAlign="center"
                    p={3}
                  >
                    {source === physicianConst.COMPLETED ? (
                      <>
                        {' '}
                        {completedPhysicianDocument?.loading ? (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: 250,
                              width: '100%',
                            }}
                          >
                            <img
                              src={LoaderIcon}
                              alt="loader.."
                            />
                          </div>
                        ) : completedPhysicianDocument?.totalDocument?.length === 0 ? (
                          <Box>
                            <img
                              src={emptyImg}
                              alt="loader"
                              style={{
                                width: 720,
                                height: 300,
                                margin: 'auto',
                                alignItems: 'center',
                                alignContent: 'center',
                                marginTop: 100,
                                display: 'block',
                              }}
                            />

                            <Typography
                              color={'text.secondary'}
                              variant="h5"
                              fontWeight={'200'}
                            >
                              No results found
                            </Typography>
                          </Box>
                        ) : null}
                      </>
                    ) : (
                      <>
                        {' '}
                        {allDocumentsOfPhysician?.loading ? (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: 250,
                              width: '100%',
                            }}
                          >
                            <img
                              src={LoaderIcon}
                              alt="loader.."
                            />
                          </div>
                        ) : allDocumentsOfPhysician?.totalDocument?.length === 0 ? (
                          <Box>
                            <img
                              src={emptyImg}
                              alt="loader"
                              style={{
                                width: 720,
                                height: 300,
                                margin: 'auto',
                                alignItems: 'center',
                                alignContent: 'center',
                                marginTop: 100,
                                display: 'block',
                              }}
                            />

                            <Typography
                              color={'text.secondary'}
                              variant="h5"
                              fontWeight={'200'}
                            >
                              No results found
                            </Typography>
                          </Box>
                        ) : null}
                      </>
                    )}
                  </Box>
                }
              >
                <Table
                  stickyHeader
                  sx={{ minWidth: 700 }}
                >
                  <TableHead>
                    <TableRow>
                      {physicianTableHeads?.map((item: PhysicianTableObject, index: number) => {
                        const isActive = physicianDto.sortBy === item.value;
                        const currentDirection = isActive ? physicianDto.sortDirection : 'asc';
                        return (
                          <TableCell key={index}>
                            {item.label !== 'action' ? (
                              <TableSortLabel
                                active={isActive}
                                direction={currentDirection}
                                onClick={() =>
                                  item.sort &&
                                  handleSort(
                                    item.value,
                                    physicianDto.sortDirection === 'asc' ? 'desc' : 'asc'
                                  )
                                }
                              >
                                {item.label}
                              </TableSortLabel>
                            ) : (
                              <> {item.label}</>
                            )}
                            {item.sort &&
                              (item.label === 'Status' &&
                              source === physicianConst.COMPLETED ? null : !hideFilter?.includes(
                                  item?.label
                                ) ? (
                                <MultiselectFilter
                                  label={item.label}
                                  totalElements={
                                    item.label === 'Facility Name'
                                      ? getAllFacilities?.response?.data?.data?.totalElements
                                      : item.label === 'Patient'
                                      ? getAllPatient?.response?.data?.data?.totalElements
                                      : 0
                                  }
                                  appliedValues={
                                    item.label === 'Facility Name'
                                      ? physicianDto.facilityIds
                                      : item.label === 'Patient'
                                      ? physicianDto.patientIds
                                      : item.label === 'Document Type'
                                      ? physicianDto.type
                                      : item.label === 'Status'
                                      ? physicianDto.status
                                      : []
                                  }
                                  setAppliedValues={setPhysicianDto}
                                  options={
                                    item.label === 'Facility Name' || item.label === 'Patient'
                                      ? getAllFacilityList(item.label)
                                      : item.label === 'Status'
                                      ? physicianStatus
                                      : documentTypesForPhysician
                                  }
                                  handleSortMultiselect={handleSortMultiselect}
                                  handleCallTheList={handleCallTheList}
                                  handleCallNextApi={handleCallNextApi}
                                  forDynamicOption={
                                    item.label === 'Document Type' || item.label === 'Status'
                                      ? true
                                      : false
                                  }
                                  sortConfig={physicianDto}
                                />
                              ) : item.label === 'Days Remaining' ? null : (
                                <MenuDateFilter
                                  label={item.label}
                                  setSelectedDueDate={setPhysicianDto}
                                  selectedDueDate={
                                    item.label === 'Days Remaining'
                                      ? getTheDates(
                                          physicianDto.startDueDate,
                                          physicianDto.endDueDate
                                        )
                                      : item.label === 'DOS'
                                      ? getTheDates(
                                          physicianDto.startDateOfService,
                                          physicianDto.endDateOfService
                                        )
                                      : null
                                  }
                                />
                              ))}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {source === physicianConst.COMPLETED ? (
                      <>
                        {completedPhysicianDocument?.totalDocument?.map((document: any) => {
                          return (
                            document.status !== 'COMPLETED' && (
                              <TableRow
                                hover
                                key={document?.uuid}
                              >
                                <TableCell>
                                  <Box>
                                    <Typography variant="subtitle2">
                                      {document?.patientName}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2">
                                    {' '}
                                    {document?.facilityName}
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  <Stack
                                    alignItems="center"
                                    direction="row"
                                    spacing={1}
                                  >
                                    <Grid
                                      container
                                      sx={{ alignItems: 'center' }}
                                    >
                                      <Box sx={{ marginRight: '4px', marginTop: '7px' }}>
                                        <DocumentIcons
                                          iconType={document?.formType
                                            ?.split('_')?.[0]
                                            ?.toLowerCase()}
                                        />
                                      </Box>

                                      <Box sx={{ display: 'flex' }}>
                                        <Typography variant="subtitle2">
                                          {document?.documentType
                                            ? formatString(document?.documentType)
                                            : '-'}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="subtitle2">
                                      {document?.dateOfService
                                        ? format(
                                            new Date(
                                              new Date(document?.dateOfService)?.valueOf() +
                                                new Date(
                                                  document?.dateOfService
                                                )?.getTimezoneOffset() *
                                                  60 *
                                                  1000
                                            ),
                                            'dd MMM, yyyy'
                                          )
                                        : '-'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="left">
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    {/* <Typography variant="subtitle2">
                                    {document?.dueDate
                                      ? format(
                                          new Date(
                                            new Date(document?.dueDate)?.valueOf() +
                                              new Date(document?.dueDate)?.getTimezoneOffset() *
                                                60 *
                                                1000
                                          ),
                                          'dd MMM, yyyy'
                                        )
                                      : '-'}
                                  </Typography> */}
                                    <Typography
                                      sx={{ color: '#6C737F', textAlign: 'center' }}
                                      variant="caption"
                                    >
                                      {/* {getDisplayString(document?.dueDate)} */}
                                      {document?.dateOfService
                                        ? calculateDaysRemaining(document?.dateOfService)
                                        : '-'}
                                    </Typography>
                                  </Box>
                                </TableCell>

                                <TableCell>
                                  <Box sx={{ display: 'flex', ml: '-20px' }}>
                                    <Chip
                                      label={<Typography variant="overline">SIGNED</Typography>}
                                      variant="outlined"
                                      sx={{
                                        marginLeft: '10px',
                                        color: '#107569',
                                        backgroundColor: 'rgba(21, 183, 158, 0.12)',
                                      }}
                                    />
                                  </Box>
                                </TableCell>

                                <TableCell align="left">
                                  {' '}
                                  <IconButton
                                    title="View Document"
                                    onClick={() => previewDocument(document)}
                                  >
                                    <VisibilityOutlined />
                                  </IconButton>
                                  {/* <IconButton
                                  title="Sign Document"
                                  onClick={() => handleSignatureNavigation(document)}
                                >
                                  <SvgIcon>
                                    <BorderColorOutlinedIcon />
                                  </SvgIcon>
                                </IconButton> */}
                                </TableCell>
                              </TableRow>
                            )
                          );
                        })}
                      </>
                    ) : (
                      <>
                        {allDocumentsOfPhysician?.totalDocument?.map((document: any) => {
                          return (
                            document.status !== 'COMPLETED' && (
                              <TableRow
                                hover
                                key={document?.uuid}
                              >
                                <TableCell>
                                  <Box>
                                    <Typography variant="subtitle2">
                                      {document?.patientName}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="subtitle2">
                                    {' '}
                                    {document?.facilityName}
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  <Stack
                                    alignItems="center"
                                    direction="row"
                                    spacing={1}
                                  >
                                    <Grid
                                      container
                                      sx={{ alignItems: 'center' }}
                                    >
                                      <Box sx={{ marginRight: '4px', marginTop: '7px' }}>
                                        <DocumentIcons
                                          iconType={document?.formType
                                            ?.split('_')?.[0]
                                            ?.toLowerCase()}
                                        />
                                      </Box>

                                      <Box sx={{ display: 'flex' }}>
                                        <Typography variant="subtitle2">
                                          {document?.documentType
                                            ? formatString(document?.documentType)
                                            : '-'}
                                        </Typography>
                                      </Box>
                                    </Grid>
                                  </Stack>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant="subtitle2">
                                      {document?.dateOfService
                                        ? format(
                                            new Date(
                                              new Date(document?.dateOfService)?.valueOf() +
                                                new Date(
                                                  document?.dateOfService
                                                )?.getTimezoneOffset() *
                                                  60 *
                                                  1000
                                            ),
                                            'dd MMM, yyyy'
                                          )
                                        : '-'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="left">
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    {/* <Typography variant="subtitle2">
                                    {document?.dueDate
                                      ? format(
                                          new Date(
                                            new Date(document?.dueDate)?.valueOf() +
                                              new Date(document?.dueDate)?.getTimezoneOffset() *
                                                60 *
                                                1000
                                          ),
                                          'dd MMM, yyyy'
                                        )
                                      : '-'}
                                  </Typography> */}
                                    <Typography
                                      sx={{ color: '#6C737F', textAlign: 'center' }}
                                      variant="caption"
                                    >
                                      {/* {getDisplayString(document?.dueDate)} */}
                                      {document?.dateOfService
                                        ? calculateDaysRemaining(document?.dateOfService)
                                        : '-'}
                                    </Typography>
                                  </Box>
                                </TableCell>

                                <TableCell>
                                  <Box sx={{ display: 'flex', ml: '-20px' }}>
                                    {'OVERDUE' === document?.status ? (
                                      <Chip
                                        label={
                                          <Typography variant="overline">
                                            {document?.status}
                                          </Typography>
                                        }
                                        variant="outlined"
                                        sx={{
                                          marginLeft: '10px',
                                          color: '#B42318',
                                          backgroundColor: '#F044381F',
                                        }}
                                      />
                                    ) : 'URGENT' === document?.status ? (
                                      <Chip
                                        label={
                                          <Typography variant="overline">
                                            {document?.status}
                                          </Typography>
                                        }
                                        variant="outlined"
                                        sx={{
                                          marginLeft: '10px',
                                          color: '#B54708',
                                          backgroundColor: '#F790091F',
                                        }}
                                      />
                                    ) : 'UPCOMING' === document?.status ? (
                                      <Chip
                                        label={
                                          <Typography variant="overline">
                                            {document?.status}
                                          </Typography>
                                        }
                                        variant="outlined"
                                        sx={{
                                          marginLeft: '10px',
                                          color: '#6366F1',
                                          backgroundColor: '#EBEEFE',
                                        }}
                                      />
                                    ) : 'AWAITING SIGNATURE' === document?.status ||
                                      'NEEDS APPROVED' === document?.status ? (
                                      <Chip
                                        label={
                                          <Typography variant="overline">
                                            {document?.status}
                                          </Typography>
                                        }
                                        variant="outlined"
                                        sx={{
                                          marginLeft: '10px',
                                          color: '#6C737F',
                                          backgroundColor: 'rgba(17, 25, 39, 0.08)',
                                        }}
                                      />
                                    ) : (
                                      '-'
                                    )}
                                  </Box>
                                </TableCell>

                                <TableCell align="left">
                                  {' '}
                                  <IconButton
                                    title="View Document"
                                    onClick={() => previewDocument(document)}
                                  >
                                    <VisibilityOutlined />
                                  </IconButton>
                                  <IconButton
                                    title="Sign Document"
                                    onClick={() => handleSignatureNavigation(document)}
                                  >
                                    <SvgIcon>
                                      <BorderColorOutlinedIcon />
                                    </SvgIcon>
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            )
                          );
                        })}
                      </>
                    )}
                  </TableBody>
                </Table>
              </InfiniteScroll>
            </Box>
          </TableContainer>
        </Stack>
      </Scrollbar>
    </Box>
  );
};

export default PhysicianDocumentsTable;
